import { BrowserRouter, Route, Routes } from 'react-router-dom';

import RequireAuth from '../auth/RequireAuth';
import GuestGuard from '../auth/GuestGuard';
import { ADMIN_ROLES, ROLES } from '../config';

// Layouts
import ClientLayout from '../layouts/client';
import DashboardLayout from '../layouts/dashboard';
import AuthLayout from '../layouts/auth';

// Paginas de autenticacion
import LoginPage from '../pages/auth/LoginPage';
import RegisterPage from '../pages/auth/RegisterPage';
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '../pages/auth/ResetPasswordPage';

// Paginas cliente (tienda publica)
import HomePage from '../pages/client/HomePage';
import CatalogPage from '../pages/client/CatalogPage';
import ProductDetailPage from '../pages/client/ProductDetailPage';
import QuoteBuilderPage from '../pages/client/QuoteBuilderPage';
import QuoteHistoryPage from '../pages/client/QuoteHistoryPage';
import ServiceRequestPage from '../pages/client/ServiceRequestPage';
import ServiceOrderTrackingPage from '../pages/client/ServiceOrderTrackingPage';
import CartPage from '../pages/client/CartPage';
import CheckoutPage from '../pages/client/CheckoutPage';
import OrderHistoryPage from '../pages/client/OrderHistoryPage';
import AccountPage from '../pages/client/AccountPage';
import CustomerDashboardPage from '../pages/client/CustomerDashboardPage';
import DistributorCatalogPage from '../pages/client/DistributorCatalogPage';
import OffersPage from '../pages/client/OffersPage';
import AboutPage from '../pages/client/AboutPage';

// Paginas admin (panel de administracion)
import DashboardHomePage from '../pages/admin/DashboardHomePage';
import ProfilePage from '../pages/admin/ProfilePage';
import DeliveryDistrictsPage from '../pages/admin/DeliveryDistrictsPage';
import ProductsListPage from '../pages/admin/products/ProductsListPage';
import DistributorPricesPage from '../pages/admin/products/DistributorPricesPage';
import CategoriesPage from '../pages/admin/products/CategoriesPage';
import BrandsPage from '../pages/admin/products/BrandsPage';
import QuotesListPage from '../pages/admin/quotes/QuotesListPage';
import AdminQuoteBuilderPage from '../pages/admin/quotes/QuoteBuilderPage';
import OrdersListPage from '../pages/admin/orders/OrdersListPage';
import ExpressSalePage from '../pages/admin/orders/ExpressSalePage';
import InvoicesListPage from '../pages/admin/invoices/InvoicesListPage';
import PayableCreditsListPage from '../pages/admin/finance/PayableCreditsListPage';
import CustomerCreditsListPage from '../pages/admin/finance/CustomerCreditsListPage';
import ServicesListPage from '../pages/admin/services/ServicesListPage';
import ServiceOrdersListPage from '../pages/admin/service-orders/ServiceOrdersListPage';
import GarantiasListPage from '../pages/admin/garantias/GarantiasListPage';
import UsersListPage from '../pages/admin/users/UsersListPage';
import ExchangeRateSettingsPage from '../pages/admin/settings/ExchangeRateSettingsPage';

// Paginas de error
import NotFoundPage from '../pages/NotFoundPage';
import ForbiddenPage from '../pages/ForbiddenPage';

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Tienda publica (layout "client") */}
        <Route element={<ClientLayout />}>
          <Route index element={<HomePage />} />
          <Route path="catalogo" element={<CatalogPage />} />
          <Route path="ofertas" element={<OffersPage />} />
          <Route path="acerca-de-nosotros" element={<AboutPage />} />
          <Route path="productos/:id" element={<ProductDetailPage />} />
          <Route path="cotizador" element={<QuoteBuilderPage />} />
          <Route path="servicio-tecnico" element={<ServiceRequestPage />} />
          <Route path="carrito" element={<CartPage />} />
          {/* El checkout admite compra de invitado o con cuenta: no exige sesion iniciada */}
          <Route path="checkout" element={<CheckoutPage />} />

          {/* Rutas del cliente que requieren sesion iniciada */}
          <Route element={<RequireAuth allowedRoles={[ROLES.CLIENTE]} />}>
            <Route path="mi-panel" element={<CustomerDashboardPage />} />
            <Route path="mi-cuenta" element={<AccountPage />} />
            <Route path="mis-cotizaciones" element={<QuoteHistoryPage />} />
            <Route path="mis-ordenes-servicio" element={<ServiceOrderTrackingPage />} />
            <Route path="mis-pedidos" element={<OrderHistoryPage />} />
          </Route>

          {/* Catalogo exclusivo del rol distribuidor: solo consulta, con sus propios precios */}
          <Route element={<RequireAuth allowedRoles={[ROLES.DISTRIBUIDOR]} />}>
            <Route path="distribuidor" element={<DistributorCatalogPage />} />
          </Route>
        </Route>

        {/* Autenticacion (layout "auth"), solo para visitantes no logueados */}
        <Route element={<AuthLayout />}>
          <Route element={<GuestGuard />}>
            <Route path="login" element={<LoginPage />} />
            <Route path="registro" element={<RegisterPage />} />
            <Route path="recuperar-password" element={<ForgotPasswordPage />} />
            <Route path="restablecer-password" element={<ResetPasswordPage />} />
          </Route>
        </Route>

        {/* Panel de administracion (layout "dashboard"), solo administrador/tecnico */}
        <Route path="admin" element={<RequireAuth allowedRoles={ADMIN_ROLES} />}>
          <Route element={<DashboardLayout />}>
            <Route index element={<DashboardHomePage />} />
            <Route path="perfil" element={<ProfilePage />} />
            <Route path="productos" element={<ProductsListPage />} />
            <Route path="precios-distribuidor" element={<DistributorPricesPage />} />
            <Route path="categorias" element={<CategoriesPage />} />
            <Route path="marcas" element={<BrandsPage />} />
            <Route path="cotizaciones" element={<QuotesListPage />} />
            <Route path="cotizador" element={<AdminQuoteBuilderPage />} />
            <Route path="servicios" element={<ServicesListPage />} />
            <Route path="ordenes-servicio" element={<ServiceOrdersListPage />} />

            {/* Solo administrador */}
            <Route element={<RequireAuth allowedRoles={[ROLES.ADMINISTRADOR]} />}>
              <Route path="venta-express" element={<ExpressSalePage />} />
              <Route path="pedidos" element={<OrdersListPage />} />
              <Route path="comprobantes" element={<InvoicesListPage />} />
              <Route path="creditos" element={<CustomerCreditsListPage />} />
              <Route path="creditos-por-pagar" element={<PayableCreditsListPage />} />
              <Route path="delivery" element={<DeliveryDistrictsPage />} />
              <Route path="garantias" element={<GarantiasListPage />} />
              <Route path="usuarios" element={<UsersListPage />} />
              <Route path="configuracion" element={<ExchangeRateSettingsPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="403" element={<ForbiddenPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
